<template>
  <Dialog :open="!state.did_start_game" :initial-focus="noFocusRef">
    <div ref="noFocusRef" class="fixed inset-0 flex items-center justify-center p-4">
      <!-- Version display in bottom-right corner -->
      <div class="fixed bottom-4 right-6 text-xs text-neutral-500">v{{ appVersion }}</div>
      <DialogPanel class="border border-white w-[800px] max-h-[90vh] flex flex-col">
        <DialogTitle class="relative border-b border-white">
          <img
            :src="`${Config.DOM_IMG_PATH}/banner_800x150${Config.LOCALE === 'ru' ? '_ru' : ''}.jpg`"
          />
        </DialogTitle>

        <!-- demo mode info -->
        <div v-if="Config.DEMO_MODE" class="w-full p-6 pb-0">
          <div class="w-full p-3 border border-red-600 rounded-md">
            <div class="w-full text-red-400">*** {{ text('ui.demo_mode.title') }} ***</div>
            <div class="text-sm">
              <ul class="list-outside list-disc pl-8">
                <li>{{ text('ui.demo_mode.info_1') }}</li>
                <li>{{ text('ui.demo_mode.info_2') }}</li>
                <li>{{ text('ui.demo_mode.info_3') }}</li>
                <li>{{ text('ui.demo_mode.info_4') }}</li>
              </ul>
            </div>
            <div class="mt-4 text-sm">
              <div
                class="text-cyan-300"
                v-html="Tools.linkifyText(text('ui.demo_mode.appeal_1'), false)"
              ></div>
              <div v-html="Tools.linkifyText(text('ui.demo_mode.appeal_2'), false)"></div>
            </div>
          </div>
        </div>

        <div class="py-6 w-full flex justify-center">
          <div
            v-if="!state.assets_loaded"
            class="w-full h-[64px] flex flex-col text-center text-sm"
          >
            {{ text('ui.loading') }}
            <DTSpinner class="-mt-[85px]" />
          </div>
          <!-- play button -->
          <DTButton v-else class="w-52 px-8 py-4 text-2xl" type="success" @click="onPlayBtnClicked">
            {{ text('ui.PLAY') }}
          </DTButton>
        </div>

        <!-- scrollable announcements/info area -->
        <div v-if="Config.PLATFORM !== 'yandex'" class="p-6 pt-0 overflow-auto">
          <!-- accouncements section -->
          <div :class="classes.section_title">
            {{ text('ui.announcements') }}
          </div>
          <div v-if="Config.LOCALE === 'ru'">
            <div class="text-sm text-cyan-300 mt-4">
              <p class="italic text-white">~ Вики выставлена на усыновление ~</p>
              <p>
                <a
                  href="https://dungeon-team.fandom.com/wiki/Dungeon_Team_Wiki"
                  target="_blank"
                  class="underline"
                  >Dungeon Team Wiki</a
                >
                выставлена на усыновление! Если вы заинтересованы в поддержке и улучшении вики,
                ознакомьтесь с
                <a
                  href="https://community.fandom.com/wiki/Help:Adopting_a_community"
                  target="_blank"
                  class="underline"
                  >инструкциями по усыновлению</a
                >.
              </p>
            </div>
          </div>
          <div v-else>
            <div class="text-sm text-cyan-300 mt-4">
              <p class="italic text-white">~ Wiki Up for Adoption ~</p>
              <p>
                The
                <a
                  href="https://dungeon-team.fandom.com/wiki/Dungeon_Team_Wiki"
                  target="_blank"
                  class="underline"
                  >Dungeon Team Wiki</a
                >
                is up for adoption! If you're interested in maintaining and improving the wiki,
                check out the
                <a
                  href="https://community.fandom.com/wiki/Help:Adopting_a_community"
                  target="_blank"
                  class="underline"
                  >adoption instructions</a
                >.
              </p>
            </div>
          </div>
          <!-- <div class="text-sm">
            <p class="italic">Help spread the word about Dungeon Team!</p>
            <p>
              ...friends, this is a PvP game at heart. And PvP games are more
              fun with more players. You can help boost the signal with a
              <strong>like/rating/review/etc</strong> at these sites:
            </p>
            <div class="w-full flex justify-center">
              <a
                href="https://store.steampowered.com/app/1509060/Dungeon_Team/"
                target="_blank"
                >Steam</a
              >
              <span class="mx-2">|</span>
              <a
                href="https://galaxy.click/play/514"
                target="_blank"
                >galaxy</a
              >
              <span class="mx-2">|</span>
              <a
                href="https://wakefieldstudios.itch.io/dungeon-team"
                target="_blank"
                >itch.io</a
              >
              <span class="mx-2">|</span>
              <a
                href="https://www.incrementaldb.com/game/dungeon-team"
                target="_blank"
                >Incremental DB</a
              >
              <span class="mx-2">|</span>
              <a
                href="https://plaza.dsolver.ca/games/dungeon-team"
                target="_blank"
                >The Plaza</a
              >
              <span class="mx-2">|</span>
              <a
                href="https://www.kongregate.com/games/WakefieldStudios/dungeon-team"
                target="_blank"
                >Kongregate</a
              >
              <span class="mx-2">|</span>
              <a
                href="https://www.newgrounds.com/portal/view/690505"
                target="_blank"
                >Newgrounds</a
              >
            </div>
            <br />
            <p>Thanks for your support!</p>
          </div> -->

          <!-- devlog section -->
          <div :class="classes.section_title">
            {{ text('ui.devlog') }}
          </div>

          <!-- Dynamic devlog entries based on locale -->
          <component v-for="(entry, index) in devlogEntries" :key="index" :is="entry" />
        </div>
      </DialogPanel>
    </div>
  </Dialog>
</template>

<script setup>
import { onBeforeUpdate, onMounted, onBeforeUnmount, reactive, computed, ref } from 'vue';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/vue';
import Audio from '~/Audio';
import { Config } from '~/constants';
import AccountActions from '~/flux/actions/AccountActions';
import { ApplicationStore } from '~/flux/stores';
import text from '~/text';
import Tools from '~/Tools';
import { DTButton, DTSpinner } from '~/view/components/common/DOM';
import devlogEntriesEn from './devlog-entries/en';
import devlogEntriesRu from './devlog-entries/ru';

const props = defineProps({
  onClose: Function,
});

const noFocusRef = ref(null);

const state = reactive({
  did_start_game: false,
  show_changelog: {},
  assets_loaded: false,
});

const classes = {
  section_title: 'mt-12 text-yellow-300 font-bold text-lg underline',
};

const devlogEntries = computed(() => {
  return Config.LOCALE === 'ru' ? devlogEntriesRu : devlogEntriesEn;
});

const appVersion = __APP_VERSION__;

onMounted(() => {
  if (Config.PLATFORM === 'game_distribution') {
    let _preAdVolume;

    window['GD_OPTIONS'] = {
      gameId: '38764ebe5ecc462eaf059d604e435900',
      onEvent: (event) => {
        switch (event.name) {
          case 'SDK_GAME_START':
            // advertisement done, resume game logic and unmute audio
            if (!state.did_start_game) {
              startGame();
            }
            Audio.setMusicVolume(_preAdVolume);
            break;
          case 'SDK_GAME_PAUSE':
            // pause game logic / mute audio
            _preAdVolume = Audio.getMusicVolume();
            Audio.setMusicVolume(0);
            break;
          case 'SDK_GDPR_TRACKING':
            // this event is triggered when your user doesn't want to be tracked
            break;
          case 'SDK_GDPR_TARGETING':
            // this event is triggered when your user doesn't want personalised targeting of ads and such
            break;
        }
      },
    };
    (function (d, s, id) {
      var js,
        fjs = d.getElementsByTagName(s)[0];
      if (d.getElementById(id)) return;
      js = d.createElement(s);
      js.id = id;
      js.src = 'https://html5.api.gamedistribution.com/main.min.js';
      fjs.parentNode.insertBefore(js, fjs);
    })(document, 'script', 'gamedistribution-jssdk');
  } else if (Config.PLATFORM === 'armor_games') {
    document.domain = 'armorgames.com';
    (function () {
      var ag = null;
      document.addEventListener('DOMContentLoaded', function (event) {
        var agiChecks = 0;
        function checkForAGI() {
          if (agiChecks > 1000) return;

          try {
            if (typeof parent.parent.agi !== 'undefined') {
              ag = new ArmorGames({
                user_id: parent.parent.apiAuth.user_id,
                auth_token: parent.parent.apiAuth.auth_token,
                game_id: parent.parent.apiAuth.game_id,
                api_key: '0E8060CF-72E7-472D-90D2-8CEF069A2844',
                agi: parent.parent.agi,
              });

              // ... you can start doing AG requests
            } else {
              agiChecks++;
              window.setTimeout(checkForAGI, 250);
            }
          } catch (err) {
            agiChecks++;
            window.setTimeout(checkForAGI, 250);
          }
        }
        checkForAGI();
      });
    })();
  }

  state.assets_loaded = ApplicationStore.getAll().assets_loaded;
  ApplicationStore.on(ApplicationStore.ASSETS_LOADED, onAssetsLoaded);
});

onBeforeUnmount(() => {
  ApplicationStore.removeListener(ApplicationStore.ASSETS_LOADED, onAssetsLoaded);
});

function onAssetsLoaded() {
  state.assets_loaded = true;
}

function onPlayBtnClicked() {
  if (Config.PLATFORM === 'game_distribution') {
    if (typeof window.gdsdk !== 'undefined' && window.gdsdk.showAd !== 'undefined') {
      state.is_open = false;
      window.gdsdk.showAd();
    }
  } else {
    startGame();
  }
}

function startGame() {
  AccountActions.authenticate();
  state.did_start_game = true;
  props.onClose();
}
</script>

<style>
.play-btn-wrapper {
  text-align: center;
  margin-bottom: 10px;
}

.play-btn,
.play-btn:focus {
  color: green;
  padding: 5px 30px 5px 30px;
  font-size: 32px;
  font-weight: bold;
}

.welcome-section > p {
  font-size: 12px;
}

.video-wrapper {
  text-align: center;
  position: relative;
  padding-bottom: 56.25%; /* 16:9 */
  padding-top: 0px;
  height: 0;
}
.video-wrapper iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.submit-video {
  margin-top: 5px;
  font-size: 10px;
  text-align: right;
}

.patch-notes {
  font-size: 12px;
}

.help-lost-game-data {
  font-size: 10px;
  text-align: right;
}

.help-lost-game-data > a {
  color: red;
}

code {
  border: 1px solid #444;
  padding: 2px 5px;
  color: rgb(244, 163, 253);
}
</style>
